
import React, { useMemo, useRef, useState } from "react";
import { Col, Button, Row } from "reactstrap";
import { useLocation, Link } from "react-router-dom";
import "flatpickr/dist/themes/material_blue.css";
import FlatPickr from "react-flatpickr";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { getCompanies, getDocuments, getMyDocuments, getUsersData } from "store/actions";
import { DATE_PRESETS } from "helpers/helper_funcations";

const JobListGlobalFilter = ({
  dataList,
  selectTitle,
  onFilterChange,
  filterValue,
  onDateRangeChange
}) => {
  const { pathname } = useLocation();
  const fpRef = useRef(null);
  const dispatch = useDispatch();

  const usersLoading = useSelector((s) => s.Dashboard?.getUserLoading);
  const companiesLoading = useSelector((s) => s.Company?.loading);
  const documentsLoading = useSelector((s) => s.Documents?.loading);

  const { user: loggedUser } = useSelector(state => state.Login);

  const isDocs = pathname.startsWith("/resources");
  const isUsers = pathname.startsWith("/users");
  const isCompanies = pathname.startsWith("/companies");

  const loading = isUsers ? usersLoading : isCompanies ? companiesLoading : documentsLoading;

  const [selectDate, setSelectDate] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState("");

  const hasRange =
    Array.isArray(selectDate) && selectDate.length === 2 && selectDate[0] && selectDate[1];

  const fmt = (d) => moment(d).format("DD MMM YYYY");

  const presetLabel = useMemo(() => {
    if (selectedPreset === "custom") {
      if (selectDate?.length) {
        const [s, eOrNull] = selectDate;
        const e = eOrNull || s;
        return `Custom: ${fmt(s)} – ${fmt(e)}`;
      }
      return "Custom";
    }
    const found = DATE_PRESETS.find((p) => p.key === selectedPreset);
    return found?.label || "";
  }, [selectedPreset, selectDate]);

  const applyPreset = (key) => {
    setSelectedPreset(key);
    if (key === "custom") {
      setSelectDate([]);
      setTimeout(() => fpRef.current?.flatpickr?.open(), 0);
      return;
    }
    const end = moment().endOf("day");
    let start;

    switch (key) {
      case "last7":
        start = moment().subtract(6, "days").startOf("day");
        break;
      case "last14":
        start = moment().subtract(13, "days").startOf("day");
        break;
      case "last30":
        start = moment().subtract(29, "days").startOf("day");
        break;
      case "thisMonth":
        start = moment().startOf("month");
        break;
      case "lastMonth": {
        start = moment().subtract(1, "month").startOf("month");
        const endLastMonth = moment().subtract(1, "month").endOf("month");
        const dates = [start.toDate(), endLastMonth.toDate()];
        setSelectDate(dates);
        fpRef.current?.flatpickr?.setDate(dates, false);
        onDateRangeChange?.(dates);
        return;
      }
      case "ytd":
        start = moment().startOf("year");
        break;
      case "clear":
      default:
        handleRangeClear();
        return;
    }

    const dates = [start.toDate(), end.toDate()];
    setSelectDate(dates);
    fpRef.current?.flatpickr?.setDate(dates, false);
    onDateRangeChange?.(dates);
  };

  const dateChange = (dates) => {
    setSelectDate(dates);
    setSelectedPreset(dates?.length ? "custom" : "");
    onDateRangeChange?.(dates);
  };

  const handleRangeClear = () => {
    setSelectDate([]);
    setSelectedPreset("");
    fpRef.current?.flatpickr?.clear?.();
    fpRef.current?.flatpickr?.close?.();
    onDateRangeChange?.([]);
  };

  const { addTo, buttonLabel } = useMemo(() => {
    if (isDocs) return { addTo: "/add-resource", buttonLabel: "+ Add Resource" };
    if (isUsers) return { addTo: "/add-user", buttonLabel: "+ Add User" };
    return { addTo: "/add-company", buttonLabel: "+ Add Company" };
  }, [isDocs, isUsers]);

  const handleRefresh = () => {
    if (loading) return;
    if (isUsers) return dispatch(getUsersData());
    if (isCompanies) return dispatch(getCompanies());
    if (isDocs) {
      const canSeeAllDocs = loggedUser?.role === 1 || loggedUser?.role === 2;
      return dispatch(canSeeAllDocs ? getDocuments() : getMyDocuments());
    }
  };

  // domain/company/document filter options (sorted by label)
  const filterOptions = useMemo(() => {
    const input = Array.isArray(dataList) ? dataList : [];
    const toDomain = (email) => email?.split?.("@")?.[1]?.toLowerCase()?.trim() || "";
    const toStringSafe = (v) => (typeof v === "string" ? v.trim() : "");
    const seen = new Set();
    const options = [];

    const addOption = (value, label) => {
      const key = toStringSafe(value);
      if (!key || seen.has(key)) return;
      seen.add(key);
      options.push({ value: key, label: toStringSafe(label) || key });
    };

    input.forEach((item) => {
      if (typeof item === "string") {
        addOption(item, item);
      } else if (item?.id && (item?.name || item?.domain)) {
        addOption(item.id, item.name || item.domain);
      } else if (isUsers && item?.email) {
        const d = toDomain(item.email);
        if (d) addOption(d, d);
      } else if (item && typeof item === "object") {
        const label = isDocs
          ? toStringSafe(item.name) || toStringSafe(item.domain)
          : toStringSafe(item.domain) || toStringSafe(item.name) || toDomain(item.email);
        if (label) addOption(label, label);
      }
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [dataList, isDocs, isUsers]);

  return (
    <React.Fragment>
      <Row className="gx-2 gy-2 w-100 align-items-center">
        <Col xs="12" lg="9" className="pt-2 pt-lg-0">
          <Row className="gx-2 gy-2">
            {![3, 4].includes(Number(loggedUser?.role)) && (
              <Col xs="12" md="6" lg="4">
                <select
                  className="form-select"
                  style={{ borderRadius: 10 }}
                  value={filterValue || ""}
                  onChange={(e) => onFilterChange?.(e.target.value)}
                >
                  <option value="">
                    {isUsers
                      ? (selectTitle || "Select Domain")
                      : (selectTitle || "Select")}
                  </option>
                  {filterOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Col>
            )}

            <Col xs="12" md="6" lg="4" className={`${selectedPreset === "custom" ? "" : "d-none"}`}>
              <div className="position-relative">
                <FlatPickr
                  ref={fpRef}
                  className="form-control"
                  style={{ borderRadius: 10 }}
                  placeholder="Select date range"
                  options={{ mode: "range", dateFormat: "d M, Y" }}
                  value={selectDate.length ? selectDate : null}
                  onChange={dateChange}
                />
                {hasRange && (
                  <button
                    type="button"
                    aria-label="Clear date range"
                    onClick={handleRangeClear}
                    className="btn-close position-absolute top-50 end-0 translate-middle-y me-2"
                    title="Clear"
                  />
                )}
              </div>
            </Col>

            <Col xs="12" md="6" lg="4">
              <select
                className="form-select"
                value={selectedPreset || ""}
                style={{ borderRadius: 10 }}
                onChange={(e) => {
                  const val = e.target.value;
                  if (!val) return;
                  applyPreset(val);
                }}
                aria-label="Quick date range"
              >
                <option value="" disabled>
                  {presetLabel || "Select quick range"}
                </option>
                {DATE_PRESETS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </Col>
          </Row>
        </Col>

        <Col xs="12" lg="3" className="d-flex justify-content-lg-end align-items-center gap-2">
          <div onClick={handleRefresh} style={{ cursor: "pointer" }} title="Refresh">
            {!loading ? (
              <img src={require("../../assets/images/refresh.png")} height={23} alt="refresh" />
            ) : (
              <img src={require("../../assets/images/refresh.gif")} height={23} alt="refreshing" />
            )}
          </div>

          {![3, 4].includes(Number(loggedUser?.role)) && (
            <Button
              tag={Link}
              to={addTo}
              color="primary"
              style={{ borderRadius: "10px", borderWidth: 0 }}
            >
              {buttonLabel}
            </Button>
          )}
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default JobListGlobalFilter;
